import type { Command } from 'commander';
import { ALLOWED_LOCALES, loadConfig, type Locale, type CliConfig } from './config-store';
import { channelBaseUrl, currentChannel } from './channel';
import { CliError, ExitCode } from './errors';
import { resolveOutputFormat, isColorEnabled, type OutputFormat } from './output';

// Production public API. Local development overrides via the WPORT_API_BASE env var
// or the `--api` flag. (The `api_base_url` config key was removed in 0.1.2 — keeping a
// persisted, mutable base URL on disk is an SSRF / credential-exfil surface that
// external users don't need.)
// 預設 base 由 build 時烙進的 channel 決定(prod → api.wport.me、dev → developers.wport.me/v2)。
// 覆寫優先序不變:--api > WPORT_API_BASE > 此預設。base 仍不落地 config(SSRF 面不變)。
const DEFAULT_BASE_URL = channelBaseUrl(currentChannel());
export const API_BASE_ENV_VAR = 'WPORT_API_BASE';
// pm_48 交付 B(BR-039):CLI 每個請求標 `X-Source: cli`(三端 audit 鏈;後端交付 A 已上線收錄)。
// 單一常數收斂,四個出口(jobs createApiClient / enterprise transport / personal / oauth)共用。
export const CLI_SOURCE = 'cli';
const DEFAULT_LOCALE: Locale = 'zh-TW';
const DEFAULT_TIMEOUT_MS = 10_000;

export interface ResolvedContext {
	baseUrl: string;
	locale: Locale;
	timeoutMs: number;
	format: OutputFormat;
	color: boolean;
	config: CliConfig;
}

interface RawGlobals {
	lang?: string;
	api?: string;
	output?: string;
	color?: boolean;
	timeout?: number;
}

export function resolveContext(command: Command): ResolvedContext {
	const globals = command.optsWithGlobals() as RawGlobals;
	const config = loadConfig();

	return {
		baseUrl: resolveBaseUrl(globals.api),
		locale: resolveLocale(globals.lang, config),
		timeoutMs: resolveTimeout(globals.timeout, config),
		format: resolveOutputFormat(globals.output),
		color: isColorEnabled(globals.color === false),
		config,
	};
}

/**
 * Resolve the API base URL. Precedence: `--api` flag > WPORT_API_BASE env var > default.
 * The env var is just as untrusted as the (removed) config key, so it gets the same
 * http(s)-only validation to keep the SSRF surface closed.
 */
function resolveBaseUrl(override: string | undefined): string {
	const fromEnv = process.env[API_BASE_ENV_VAR]?.trim();
	if (override !== undefined) return validateBaseUrl(override, '--api');
	if (fromEnv) return validateBaseUrl(fromEnv, `${API_BASE_ENV_VAR} env var`);
	return DEFAULT_BASE_URL;
}

function validateBaseUrl(raw: string, source: string): string {
	let url: URL;
	try {
		url = new URL(raw);
	} catch {
		throw new CliError(`Invalid API base URL from ${source}: ${raw}`, ExitCode.InvalidArgument);
	}
	if (url.protocol !== 'https:' && url.protocol !== 'http:') {
		throw new CliError(
			`API base URL from ${source} must be http or https (got ${url.protocol})`,
			ExitCode.InvalidArgument
		);
	}
	return raw.replace(/\/$/, '');
}

function resolveLocale(override: string | undefined, config: CliConfig): Locale {
	const raw = override ?? config.locale ?? DEFAULT_LOCALE;
	if (!(ALLOWED_LOCALES as readonly string[]).includes(raw)) {
		throw new CliError(`Invalid --lang "${raw}". Allowed: ${ALLOWED_LOCALES.join(', ')}`, ExitCode.InvalidArgument);
	}
	return raw as Locale;
}

function resolveTimeout(override: number | undefined, config: CliConfig): number {
	const raw = override ?? config.timeout_ms ?? DEFAULT_TIMEOUT_MS;
	if (!Number.isInteger(raw) || raw < 100 || raw > 600_000) {
		throw new CliError(`Invalid --timeout ${raw} (must be integer 100..600000)`, ExitCode.InvalidArgument);
	}
	return raw;
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = {
	resolveBaseUrl,
	DEFAULT_BASE_URL,
};
